/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Animation, ColorName, ColorsCSS } from '../../../theme';
import { labelStyles } from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';

type Props = {
  /**
   * The color of the progress bar.
   * @default 'blue'
   */
  color?: ColorName;
  /**
   * The maximum value for the progress bar.
   * @default 1
   */
  max?: number;
  /**
   * The minimum value for the progress bar.
   * @default 0
   */
  min?: number;
  /**
   * The amount of the progress bar. Should be between `min` and `max`.
   */
  value: number;
};

const Styles = labelStyles('ProgressBar', {
  root: css({
    position: 'relative',
    height: 'var(--zds-size-4, 4px)',
    borderRadius: 'var(--zds-radius-small, 2.5px)',
    backgroundColor: 'var(--zds-background-stronger, #ECE9DF)',
  }),
  progress: (color: ColorName) => [
    css({
      height: 'inherit',
      borderRadius: 'inherit',
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
      backgroundColor: ColorsCSS[color],
    }),
  ],
});

/**
 * Basic progress component. Shows how full something is.
 */
export const ProgressBar = ({
  max = 1,
  min = 0,
  color = 'blue',
  ...props
}: Props) => {
  const value = clamp(props.value, min, max);
  const range = max - min;
  const decimal = (value - min) / range;
  const width = `${decimal * 100}%`;
  return (
    <div css={Styles.root} data-zds>
      <div
        css={Styles.progress(color)}
        data-testid="progress"
        data-zds
        style={{
          width,
        }}
      />
    </div>
  );
};
